import { useEffect, useState } from "react";
import { Result, Button } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useTranslation } from "react-i18next";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";

export const EmailVerified = () => {
  const [verificationStatus, setVerificationStatus] = useState<
    "success" | "already_verified" | "error"
  >("success");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const type = params.get("type");
        const token = params.get("token");
        const email = params.get("email");

        if (type === "signup" && token && email) {
          // First verify the email with the token
          const { error: verifyError } = await supabase.auth.verifyOtp({
            email,
            token,
            type: "signup",
          });

          if (verifyError) {
            console.error("Verification error:", verifyError);
            throw verifyError;
          }

          // Wait a moment for Supabase to update the user's status
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // After successful verification, check if the email is already verified
          const { error } = await supabase.auth.getUser();

          if (error) throw error;

          // If we get here, the verification was successful
          setVerificationStatus("success");
        } else {
          setVerificationStatus("error");
        }
      } catch (error) {
        console.error("Error verifying email:", error);
        setVerificationStatus("error");
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [location]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <div>Verifying your email...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "20px",
      }}
    >
      <Result
        icon={
          verificationStatus === "success" ? (
            <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a" }} />
          ) : verificationStatus === "already_verified" ? (
            <InfoCircleOutlined style={{ fontSize: 64, color: "#1890ff" }} />
          ) : null
        }
        status={verificationStatus === "error" ? "error" : "success"}
        title={
          verificationStatus === "success"
            ? t("auth.emailVerifiedTitle")
            : verificationStatus === "already_verified"
            ? t("auth.emailAlreadyVerifiedTitle")
            : "Verification Failed"
        }
        subTitle={
          verificationStatus === "success"
            ? t("auth.emailVerifiedMessage")
            : verificationStatus === "already_verified"
            ? t("auth.emailAlreadyVerifiedMessage")
            : "There was an error verifying your email. Please try again or contact support."
        }
        extra={[
          <Button type="primary" key="login" onClick={() => navigate("/auth")}>
            Go to Login
          </Button>,
        ]}
      />
    </div>
  );
};
